import React from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { connect as connectRedux } from 'react-redux'
import { compose } from 'recompose'
import _pick from 'lodash/pick'
import _trim from 'lodash/trim'
import connectHistory from 'gui/components/router/connect'
import { getConfigDataServer } from 'services/config'
import { TYPE_TESTAROSSA, TYPE_WEB_APP, TYPE_BRIGHTCOVE_STUDIO } from './types'
import { LINK_TARGET_BLANK } from './targets'

/**
 * Create valid href for a link
 * @param {Object} options The options
 * @param {import('immutable').Map} options.config The config state
 * @param {String} options.type The link type
 * @param {String} options.href The href
 */
function createHref (options = {}) {
  const { config, type, absolute } = options
  let { href } = options
  if (absolute) {
    return href
  }

  href = _trim(href, '/')
  if (!type) {
    return `/${href}`
  }
  const server = getConfigDataServer(config, type)
  if (!server) {
    throw new Error(`The link type ${type} does not have a config server entry`)
  }
  return `${server}/${href}`
}

/**
 * Handle link onClick event
 * @param {Event} e The onClick event
 * @param {Object} props The component props
 * @param {String} props.href The href to navigate to when clicked
 * @param {String} [props.type] A link type to add the service sepecific
 * behaviors and server url prefix to href
 * @param {History} props.history The history singleton
 * @param {Boolean} [props.absolute] When true navigate using default browser
 * url navigation
 */
function onClick (e, props) {
  const { history, absolute, type, href } = props
  // If this is absolute link or type link perform a default browser navigation
  if (absolute || type) {
    return
  }
  // Allow modifier-key clicks (Cmd/Ctrl/Shift) and middle-clicks to use native
  // browser behavior so users can open links in new tabs/windows
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) {
    return
  }
  e.preventDefault()
  history.push(createHref({ href, type }))
}

/**
 * A component which creates a navigational link
 * @param {Object} props The react props
 * @param {String} props.href The href to navigate to when clicked
 * @param {String} [props.type] A link type to add the service sepecific
 * behaviors and server url prefix to href
 * @param {import('immutable').Map} props.config The config state
 * @param {History} props.history The history singleton
 * @param {Boolean} [props.absolute] When true navigate using default browser
 * url navigation
 * @param {String} [props.target] The link target attribute
 */
function Link (props) {
  const { type, config, href, children, className, target, absolute } = props
  // Transform LINK_TARGET_BLANK ('blank') to HTML standard '_blank'
  const htmlTarget = target === LINK_TARGET_BLANK ? '_blank' : target
  return (
    <a
      href={createHref({
        href,
        type,
        config,
        absolute,
      })}
      onClick={(e) => onClick(e, props)}
      className={className}
      target={htmlTarget}
    >
      {children}
    </a>
  )
}

Link.propTypes = {
  href: PropTypes.string,
  type: PropTypes.oneOf([TYPE_TESTAROSSA, TYPE_WEB_APP, TYPE_BRIGHTCOVE_STUDIO]),
  config: ImmutablePropTypes.map.isRequired,
  history: PropTypes.object.isRequired,
  absolute: PropTypes.bool,
  target: PropTypes.oneOf([LINK_TARGET_BLANK]),
}

export default compose(
  connectRedux((state) => _pick(state, ['config'])),
  connectHistory(),
)(Link)
